import React, { useEffect, useMemo, useState } from 'react';
import { acordApi, fetchBlobUrl, apiErrorMessage } from '../../services/agentApi';
import { btn, input, Spinner } from '../agent/agentUi';

interface FieldEntry {
  name: string;
  type: string;
  page: number | null;
  rect: [number, number, number, number] | null;
  options?: string[];
}

interface MapEntry {
  fields: string[];
  transform?: string;
  whenValue?: string;
}

interface Props {
  form: { id: string; formNumber: string; fieldSchema: FieldEntry[]; fieldMap: Record<string, MapEntry>; mappingStatus: string; fillable: boolean };
  onSaved: () => void;
}

const TRANSFORMS = ['', 'upper', 'date_mmddyyyy', 'currency', 'checkbox'];

/**
 * Semantic key ↔ PDF field mapping for one ACORD edition (Phase 4): AI
 * "propose mapping", per-key field pickers, validation on save, and a
 * test-fill that renders the PDF with sample values (never persisted).
 */
const AcordMappingEditor: React.FC<Props> = ({ form, onSaved }) => {
  const [keys, setKeys] = useState<Array<{ key: string; label: string; group: string; kind?: string; hint?: string }>>([]);
  const [map, setMap] = useState<Record<string, MapEntry>>(form.fieldMap || {});
  const [confidence, setConfidence] = useState<Record<string, number>>({});
  const [filter, setFilter] = useState('');
  const [onlyMapped, setOnlyMapped] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [samples, setSamples] = useState<Record<string, string>>({});
  const [testUrl, setTestUrl] = useState<string | null>(null);

  useEffect(() => {
    acordApi.semanticKeys().then((r) => setKeys(r.keys)).catch(() => setKeys([]));
  }, []);
  useEffect(() => setMap(form.fieldMap || {}), [form.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fieldNames = useMemo(() => (form.fieldSchema || []).filter((f) => !['button', 'signature', 'unknown'].includes(f.type)).map((f) => f.name).sort(), [form.fieldSchema]);
  const fieldByName = useMemo(() => new Map((form.fieldSchema || []).map((f) => [f.name, f])), [form.fieldSchema]);
  const usedFields = useMemo(() => new Set(Object.values(map).flatMap((e) => e.fields)), [map]);

  const visibleKeys = keys.filter((k) => {
    if (onlyMapped && !(map[k.key]?.fields?.length)) return false;
    if (!filter.trim()) return true;
    const f = filter.toLowerCase();
    return k.key.toLowerCase().includes(f) || k.label.toLowerCase().includes(f) || (map[k.key]?.fields || []).some((x) => x.toLowerCase().includes(f));
  });

  const setEntry = (key: string, entry: MapEntry | null) => {
    setMap((m) => {
      const next = { ...m };
      if (!entry || entry.fields.length === 0) delete next[key];
      else next[key] = entry;
      return next;
    });
    setNotice(null);
  };

  const propose = async () => {
    setBusy('propose');
    setError(null);
    try {
      const r = await acordApi.proposeMapping(form.id);
      setMap((m) => ({ ...r.fieldMap, ...m }));
      setConfidence(r.confidence || {});
      setWarnings(r.warnings || []);
      setNotice(`AI proposed ${Object.keys(r.fieldMap).length} mappings (existing ones kept). Review, then save.`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Proposal failed'));
    } finally {
      setBusy(null);
    }
  };

  const save = async (status?: 'draft' | 'verified') => {
    setBusy('save');
    setError(null);
    try {
      const r = await acordApi.saveMapping(form.id, map, status);
      setWarnings(r.warnings || []);
      setNotice(`Saved (${r.form.mappingStatus}).`);
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err, 'Save failed'));
    } finally {
      setBusy(null);
    }
  };

  const testFill = async () => {
    setBusy('test');
    setError(null);
    try {
      const values: Record<string, string> = {};
      for (const k of Object.keys(map)) {
        const meta = keys.find((x) => x.key === k);
        values[k] = samples[k] ?? (meta?.kind === 'checkbox' ? 'true' : meta?.kind === 'date' ? '01/01/2027' : meta?.kind === 'currency' ? '1000000' : (meta?.label || k).slice(0, 30));
      }
      if (testUrl) URL.revokeObjectURL(testUrl);
      const url = await fetchBlobUrl(acordApi.testFillUrl(form.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fieldMap: map, values }),
      });
      setTestUrl(url);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      setError(apiErrorMessage(err, 'Test fill failed'));
    } finally {
      setBusy(null);
    }
  };

  const labeledCount = (form.fieldSchema || []).filter((f: any) => typeof f.label === 'string').length;
  const labelFields = async () => {
    setBusy('label');
    setError(null);
    try {
      const r = await acordApi.labelFields(form.id);
      setNotice(`Read labels for ${r.labeled} of ${r.total} fields (${r.excluded} marked office/carrier use). Fills now cover every field, mapped or not.`);
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err, 'Labeling failed'));
    } finally {
      setBusy(null);
    }
  };

  if (!form.fillable) return <p className="text-sm text-gray-500">This edition is not fillable, so it can't be mapped.</p>;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button className={btn.secondary} onClick={labelFields} disabled={!!busy} title="AI reads every field's printed label and section, so fills can complete fields that have no semantic key">{busy === 'label' ? <><Spinner className="w-3 h-3" /> Reading labels…</> : `🏷️ ${labeledCount ? 'Re-read' : 'Read'} field labels (AI)`}</button>
        <button className={btn.secondary} onClick={propose} disabled={!!busy}>{busy === 'propose' ? <><Spinner className="w-3 h-3" /> Proposing…</> : '✨ Propose mapping (AI)'}</button>
        <button className={btn.secondary} onClick={testFill} disabled={!!busy || Object.keys(map).length === 0}>{busy === 'test' ? 'Rendering…' : 'Test fill'}</button>
        <button className={btn.primary} onClick={() => save('draft')} disabled={!!busy}>Save draft</button>
        <button className={btn.success} onClick={() => save('verified')} disabled={!!busy || Object.keys(map).length === 0}>Save as verified</button>
        <span className="text-xs text-gray-500">{Object.keys(map).length} keys mapped · {usedFields.size}/{fieldNames.length} fields used · status: {form.mappingStatus} · {labeledCount} fields labeled</span>
      </div>
      <div className="flex items-center gap-2">
        <input className={`${input} max-w-xs`} placeholder="Filter keys or fields…" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <label className="text-xs flex items-center gap-1.5 text-gray-600 dark:text-gray-300"><input type="checkbox" checked={onlyMapped} onChange={(e) => setOnlyMapped(e.target.checked)} /> Mapped only</label>
      </div>
      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      {notice && <p className="text-xs text-green-700 dark:text-green-400">{notice}</p>}
      {warnings.length > 0 && (
        <details className="text-xs text-amber-700 dark:text-amber-300">
          <summary>{warnings.length} warning(s)</summary>
          <ul className="list-disc ml-5">{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </details>
      )}
      <datalist id={`acord-fields-${form.id}`}>
        {fieldNames.map((n) => <option key={n} value={n} />)}
      </datalist>
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
            <tr>
              <th className="text-left font-medium px-2 py-1.5 w-1/3">Semantic key</th>
              <th className="text-left font-medium px-2 py-1.5">PDF field(s)</th>
              <th className="text-left font-medium px-2 py-1.5">Transform</th>
              <th className="text-left font-medium px-2 py-1.5">When value</th>
              <th className="text-left font-medium px-2 py-1.5">Sample</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {visibleKeys.map((k) => {
              const entry = map[k.key];
              const fields = entry?.fields || [];
              return (
                <tr key={k.key} className={fields.length ? '' : 'opacity-70'}>
                  <td className="px-2 py-1.5 align-top">
                    <div className="font-mono text-[11px] text-gray-800 dark:text-gray-100">{k.key}</div>
                    <div className="text-gray-500">{k.label}{confidence[k.key] !== undefined && <span className="ml-1 text-purple-600">({Math.round(confidence[k.key] * 100)}%)</span>}</div>
                  </td>
                  <td className="px-2 py-1.5 align-top">
                    <div className="flex flex-wrap gap-1">
                      {fields.map((f) => (
                        <span key={f} className="inline-flex items-center gap-1 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded px-1.5 py-0.5 font-mono text-[11px]" title={`${fieldByName.get(f)?.type || '?'} · page ${fieldByName.get(f)?.page ?? '?'}`}>
                          {f}
                          <button className="text-gray-400 hover:text-red-600 dark:text-red-400" onClick={() => setEntry(k.key, { ...(entry as MapEntry), fields: fields.filter((x) => x !== f) })}>×</button>
                        </span>
                      ))}
                      <input
                        list={`acord-fields-${form.id}`}
                        className="min-w-[10rem] flex-1 text-[11px] px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        placeholder="+ field"
                        onKeyDown={(e) => {
                          const v = (e.target as HTMLInputElement).value.trim();
                          if (e.key === 'Enter' && v && fieldByName.has(v)) {
                            setEntry(k.key, { ...(entry || {}), fields: Array.from(new Set([...fields, v])) } as MapEntry);
                            (e.target as HTMLInputElement).value = '';
                          }
                        }}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v && fieldByName.has(v)) {
                            setEntry(k.key, { ...(entry || {}), fields: Array.from(new Set([...fields, v])) } as MapEntry);
                            e.target.value = '';
                          }
                        }}
                      />
                    </div>
                  </td>
                  <td className="px-2 py-1.5 align-top">
                    <select className="text-[11px] border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" disabled={!fields.length} value={entry?.transform || ''} onChange={(e) => setEntry(k.key, { ...(entry as MapEntry), transform: e.target.value || undefined })}>
                      {TRANSFORMS.map((t) => <option key={t} value={t}>{t || '—'}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1.5 align-top">
                    <input className="w-24 text-[11px] px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" disabled={!fields.length} value={entry?.whenValue || ''} onChange={(e) => setEntry(k.key, { ...(entry as MapEntry), whenValue: e.target.value || undefined })} />
                  </td>
                  <td className="px-2 py-1.5 align-top">
                    <input className="w-28 text-[11px] px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" disabled={!fields.length} placeholder="auto" value={samples[k.key] || ''} onChange={(e) => setSamples((s) => ({ ...s, [k.key]: e.target.value }))} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-gray-500">Unmapped PDF fields: {fieldNames.filter((n) => !usedFields.has(n)).length}. Fields can be typed or picked from the suggestion list; press Enter to add.</p>
    </div>
  );
};

export default AcordMappingEditor;
